import { Chip, styled, Tooltip } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';

interface Props {
  label: string;
  size?: 'small' | 'medium';
  value?: string;
}

const KeyChip = styled(Chip)({
  borderBottomRightRadius: 0,
  borderTopRightRadius: 0,
});

const ValueChip = styled(Chip)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderBottomLeftRadius: 0,
  borderColor: theme.palette.action.selected,
  borderTopLeftRadius: 0,
  height: 'auto',
  '& .MuiChip-label': {
    overflow: 'visible',
    textOverflow: 'clip',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    display: 'block',
    paddingTop: '6px',
    paddingBottom: '6px',
  },
}));

export const Label: FC<Props> = ({ label, size = 'small', value }) => {
  const chipRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = (): void => {
      if (chipRef.current) {
        const labelElement = chipRef.current.querySelector('.MuiChip-label');
        if (labelElement) {
          setIsTruncated(labelElement.scrollWidth > labelElement.clientWidth);
        }
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [value]);

  return <Box sx={{ whiteSpace: 'nowrap' }}>
    <KeyChip
      data-testid="label-key"
      label={label}
      size={size}
    />
    <Tooltip title={isTruncated ? value ?? '' : ''} arrow>
      <ValueChip
        ref={chipRef}
        data-testid="label-value"
        label={value}
        size={size}
        variant="outlined"
      />
    </Tooltip>
  </Box>;
};
